import { analytics } from '../../services/analytics';
import type { SlotBattleEventName, SlotBattlePopupEventName } from '../../services/analytics/types';

import { selectBattleRivePrize, selectBattleRiveRewardArt } from './battleRive';
import { selectLeadingContender } from './battleStandings';
import { selectClosingReward } from './closingReward';
import { bundleItemsValue, selectRankPrize } from './rankPrizes';
import type { BattleClosing, SlotBattle } from './types';

/**
 * Battle of the Slots analytics (golden spec `battle_of_the_slots`).
 *
 * ## The taxonomy
 * `event_parent: tournament`. The category follows the popup-vs-page split: the opening, the result
 * popup and their CTAs are `slot_battle_popup`; the entry widget and everything on the battle page
 * (standings, leaderboard, info overlay, close) are `slot_battle_page`. The category is derived from
 * the event name here, so a call site never picks it.
 *
 * ## `battle_id`, `coin_type`, `slot_id`
 * On every event. A segment key is unique only within its run, so the id is `<runKey>:<segmentKey>` —
 * the same composite core uses for the battle's Redis hash-tag slot. GS-1032 §5 adds the run's coin and
 * the slot the event is about: the one tapped, else the winner on a closing, else the leading slot.
 *
 * ## GS-1032 §5 (contract §11)
 * The leaderboard view carries the reader's rank and what it would win; the closing and the claim carry
 * the final rank and the reward; the lock bar reports its progress; the unlock is reported once per battle.
 *
 * ## Not tracked (spec)
 * The timer, the page dots, the counting pane, the claim animation, the
 * legacy nav-rail icon. Only a `claimed` answer is reported (`reward_granted`); the wallet transaction stays
 * the backend truth.
 */

const POPUP_EVENTS = new Set<SlotBattleEventName>([
  'opening_popup',
  'lets_go_button',
  'result_popup',
  'claim_button',
  'got_it_button',
  'reward_granted',
] satisfies SlotBattlePopupEventName[]);

/** `button_text`, lowercased verbatim from the Figma copy — the curly apostrophe included. */
export const SLOT_BATTLE_BUTTON_TEXT = {
  letsGo: 'let’s go',
  claim: 'claim',
  gotIt: 'got it',
} as const;

export type SlotBattlePrizeType = 'prize_pool' | 'grand_reward';
export type SlotBattleOutcomeValue = 'won' | 'lost_with_reward' | 'lost_no_reward' | 'unknown';

const KNOWN_OUTCOMES = new Set<string>(['won', 'lost_with_reward', 'lost_no_reward']);

export const slotBattleId = (battle: Pick<SlotBattle, 'runKey' | 'segmentKey'>): string =>
  `${battle.runKey}:${battle.segmentKey}`;

/**
 * The prize panel header the opening shows: the `GRAND_PRIZE` row when the prize has bundle items the
 * row can draw (free spins among them), else the coins-only `PRIZE_POOL` panel. Same rule as
 * `buildBattleRiveData`.
 */
export const openingPrizeType = (battle: SlotBattle): SlotBattlePrizeType =>
  selectBattleRiveRewardArt(selectBattleRivePrize(battle, 'opening').bundleItems).length > 0
    ? 'grand_reward'
    : 'prize_pool';

/** The backend outcome, with `unknown` for anything this client does not know. */
export const battleOutcomeValue = (outcome: string | null | undefined): SlotBattleOutcomeValue =>
  outcome && KNOWN_OUTCOMES.has(outcome) ? (outcome as SlotBattleOutcomeValue) : 'unknown';

/** The leaderboard view: the reader's rank and what it wins. Absent while unranked. */
export type SlotBattleRankProps = {
  rank?: number;
  rank_reward_gc?: number;
  rank_reward_sc?: number;
  rank_reward_items?: string;
};

/** The closing and the claim: the rank paid (winners only) and the reward. */
export type SlotBattleRewardProps = {
  final_rank?: number;
  reward_gc?: number;
  reward_sc?: number;
  reward_items?: string;
};

export type SlotBattleLockProps = {
  progress?: number;
  threshold?: number;
};

type TrackProps = {
  event_action: 'impression' | 'click';
  click_location?: 'lobby' | 'in_slot';
  event_type?: 'prize_type' | 'battle_outcome' | 'slot_game';
  event_value?: string;
  button_text?: string;
  /** The slot the event is about, when it is not the battle's leading slot or winner (a tapped slot). */
  slot_id?: number;
} & SlotBattleRankProps &
  SlotBattleRewardProps &
  SlotBattleLockProps;

type TrackedBattle = Pick<SlotBattle, 'runKey' | 'segmentKey' | 'coinType' | 'contenders'> &
  Partial<Pick<SlotBattle, 'leaderboard' | 'closing'>>;

/**
 * The slot an event is about when the call site names none: the winner on a closing, else the leader.
 * Before any bet nobody leads, and the column is left out rather than naming the first slot.
 */
const battleSlotId = (battle: TrackedBattle): number | undefined => {
  const leader = selectLeadingContender(battle.contenders ?? []);

  return (
    battle.closing?.winner?.gameId ??
    battle.leaderboard?.leadingGameId ??
    ((leader?.sharePct ?? 0) > 0 ? leader?.gameId : undefined)
  );
};

/** The leaderboard view's rank props: the reader's position and its prize, if ranked. */
export function leaderboardRankProps(battle: Pick<SlotBattle, 'leaderboard' | 'rankPrizes'>): SlotBattleRankProps {
  const rank = battle.leaderboard?.player?.position ?? null;

  if (rank === null) {
    return {};
  }

  const prize = selectRankPrize(battle.rankPrizes, rank);

  return {
    rank,
    rank_reward_gc: prize?.gc ?? 0,
    rank_reward_sc: prize?.sc ?? 0,
    rank_reward_items: bundleItemsValue(prize?.bundleItems),
  };
}

/** The closing's rank and reward props, as the result popup and the granted reward report them. */
export function closingRewardProps(closing: BattleClosing): SlotBattleRewardProps {
  const reward = selectClosingReward(closing) ?? closing.reward;

  return {
    ...(closing.rank ? { final_rank: closing.rank } : {}),
    reward_gc: reward?.gcCoin || 0,
    reward_sc: reward?.scCoin || 0,
    reward_items: bundleItemsValue(reward?.bundleItems),
  };
}

export function trackSlotBattle(eventName: SlotBattleEventName, battle: TrackedBattle, props: TrackProps): void {
  const { slot_id: slotId, ...rest } = props;

  void analytics.sendSlotBattleEvent(eventName, {
    event_parent: 'tournament',
    event_category: POPUP_EVENTS.has(eventName) ? 'slot_battle_popup' : 'slot_battle_page',
    page_url: window.location.href,
    battle_id: slotBattleId(battle),
    coin_type: battle.coinType,
    slot_id: slotId ?? battleSlotId(battle),
    ...rest,
  });
}
